import { ProductNodeBuilderProps } from 'components/common-form/builder/form/node/ProductNodeBuilder.model';
import { useBlackoutDates } from 'components/common-form/hooks/useBlackoutDates';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import type { DatePickerSize } from 'components/shared/DatePicker/DatePicker';
import { DateRangePicker } from 'components/shared/DatePicker/DatesRangePicker';
import type { DateCalculation } from 'components/shared/DatePicker/utils';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { addMonths, addYears } from 'date-fns';
import type { IntlKey } from 'locales';
import type { DateRange, DatesRangeFieldMetadata } from 'models/forms/fields/DatesRangeFieldMetadata';
import { getDateOnlyFromIsoString } from 'utils/date';

const defaultDatePresets = {
  'date.presets.threeMonths': today => addMonths(today, 3),
  'date.presets.sixMonths': today => addMonths(today, 6),
  'date.presets.nineMonths': today => addMonths(today, 9),
  'date.presets.oneYear': today => addYears(today, 1),
  'date.presets.twoYears': today => addYears(today, 2),
  'date.presets.threeYears': today => addYears(today, 3),
  'date.presets.fiveYears': today => addYears(today, 5),
  'date.presets.tenYears': today => addYears(today, 10),
} satisfies Partial<Record<IntlKey, DateCalculation>>;

export function DatesRangeField({ node: { id }, className, workspaceType, tabId, tabIndex }: ProductNodeBuilderProps) {
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as IntlKey}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <FieldDatesRangePicker id={id} tabId={tabId} className={className} tabIndex={tabIndex} />
    </BlockOrientationFormGroup>
  );
}

type FieldDatePickerProps = {
  id: string;
  size?: DatePickerSize;
  className?: string;
  tabId?: string;
  hasError?: boolean;
  tabIndex?: number;
};
export function FieldDatesRangePicker({ id, size, className, tabId, hasError, tabIndex }: FieldDatePickerProps) {
  const [value, setValue] = useData<DateRange>(id, {});
  const isFieldInvalid = useIsFieldInvalid(id);
  const isReadonly = useBlockOrFieldReadOnly(id);
  const { allowWeekend, isClearable, startDate, endDate, calendars } = useFieldMetadata<DatesRangeFieldMetadata>(id)!;
  const blackoutDates = useBlackoutDates(calendars);

  const formattedStartDate = getDateOnlyFromIsoString(value?.start);
  const formattedEndDate = getDateOnlyFromIsoString(value?.end);

  const datePresets = Object.values(defaultDatePresets);
  const datePresetsTranslations = Object.keys(defaultDatePresets) as readonly IntlKey[];

  return (
    <DateRangePicker
      className={className}
      size={size}
      datePresetTranslations={datePresetsTranslations}
      datePresets={datePresets}
      endDate={formattedEndDate}
      startDate={formattedStartDate}
      id={id}
      onChange={range => {
        setValue({ start: range?.startDate, end: range.endDate });
      }}
      isReadonly={isReadonly}
      allowWeekend={allowWeekend}
      blackoutDates={blackoutDates}
      isClearable={isClearable ?? false}
      minDate={startDate}
      maxDate={endDate}
      hasError={isFieldInvalid || hasError === true}
      tabId={tabId}
      singleMonthCalendar
      tabIndex={tabIndex}
    />
  );
}
